import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Stack, Grid, useTheme } from '@mui/material';

interface BaseItemProps {
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string | JSX.Element;
  isLarge?: boolean;
}

const ICON_GRAY_COLOR = '800';

const BeforeItem = ({ icon, lineHeight }: { icon: React.ReactNode; lineHeight?: number }) => {
  const theme = useTheme();
  return (
    <Stack alignItems="center" spacing={0.5}>
      {(() => {
        const height = lineHeight || 124;
        const arrowHeight = 2.5;
        const arrowPath = `M2.25 ${arrowHeight}L3.44338 0L0.556624 0L1.75 ${arrowHeight}L2.25 ${arrowHeight}Z`;
        const linePath = `M2 ${arrowHeight - 0.25}L1.75 ${
          arrowHeight - 0.25
        }L1.75 ${height}L2 ${height}L2.25 ${height}L2.25 ${arrowHeight - 0.25}L2 ${arrowHeight - 0.25}Z`;
        const circlePath = `M1.75 ${height - 1}C1.75 ${height - 0.862} 1.86193 ${height - 0.75} 2 ${
          height - 0.75
        }C2.13808 ${height - 0.75} 2.25 ${height - 0.862} 2.25 ${height - 1}L1.75 ${height - 1}Z`;
        return (
          <svg width="4" height={height} viewBox={`0 0 4 ${height}`} fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d={`${arrowPath} ${circlePath} ${linePath}`} fill="#454545" />
          </svg>
        );
      })()}
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: 'transparent',
          border: '0.5px solid',
          borderColor: theme.palette.grey[ICON_GRAY_COLOR],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': {
            fontSize: '12px'
          }
        }}
      >
        {icon}
      </Box>
    </Stack>
  );
};

export const StandartItem: React.FC<BaseItemProps> = ({ icon, value, label, isLarge = false }) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [lineHeight, setLineHeight] = useState<number>(13);
  useEffect(() => {
    const calcHeight = Math.max(ref.current?.clientHeight ? ref.current.clientHeight - 24 : 13, 13);
    setLineHeight(calcHeight);
  }, [ref.current?.clientHeight]);
  return (
    <Box ref={ref} sx={{ display: 'flex', alignItems: 'start', gap: 1.25, mb: 1.25 }}>
      <BeforeItem icon={icon} lineHeight={isLarge && lineHeight ? lineHeight : 13} />
      <Box sx={{ minHeight: '50px', maxWidth: '100%' }}>
        <Typography sx={{ color: theme.palette.grey[ICON_GRAY_COLOR], fontWeight: 'light' }}> {label}</Typography>
        {value}
        {isLarge && <Box sx={{ height: '20px' }} />}
      </Box>
    </Box>
  );
};

export const InfoItem: React.FC<BaseItemProps> = ({ icon, value, label, isLarge = true }) => (
  <StandartItem icon={icon} value={value} label={label} isLarge={isLarge} />
);

export const ExpandedViewLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Grid
    container
    justifyContent="start"
    spacing={{ xs: 0, md: 1 }}
    sx={{
      width: '100%',
      m: 0,
      position: 'static'
    }}
  >
    {children}
  </Grid>
);
